package dev.lingvo.site.api

import dev.lingvo.shared.ApiErrorBody
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.JsonObject
import org.khronos.webgl.ArrayBuffer
import org.w3c.files.Blob
import org.w3c.xhr.XMLHttpRequest
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

/** One cancellable upload reports byte progress while preserving the kit's structured API errors. */
class XhrUpload {
    private var active: XMLHttpRequest? = null

    /** Uploads a browser file without base64 expansion, decoding only successful responses. */
    suspend fun <T> put(
        path: String, body: Blob, contentType: String, ifMatch: String? = null, ifNoneMatch: String? = null,
        onProgress: (sent: Long, total: Long) -> Unit = { _, _ -> },
        uploadTicket: String? = null, decode: (String) -> T,
    ): T = decodeResponse(path, send(path, body, contentType, ifMatch, ifNoneMatch, onProgress, uploadTicket), decode)

    /** Uploads renderer output without forcing a second Blob allocation. */
    suspend fun <T> put(
        path: String, body: ArrayBuffer, contentType: String, ifMatch: String? = null, ifNoneMatch: String? = null,
        onProgress: (sent: Long, total: Long) -> Unit = { _, _ -> },
        uploadTicket: String? = null, decode: (String) -> T,
    ): T = decodeResponse(path, send(path, body, contentType, ifMatch, ifNoneMatch, onProgress, uploadTicket), decode)

    /** Cancels the active request; a finished upload can safely be aborted again. */
    fun abort() { active?.abort() }

    private fun <T> decodeResponse(path: String, text: String, decode: (String) -> T): T = try {
        decode(text)
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (failure: Throwable) {
        throw invalidResponse(path, failure)
    }

    private suspend fun send(
        path: String, body: dynamic, contentType: String, ifMatch: String?, ifNoneMatch: String?,
        onProgress: (Long, Long) -> Unit, uploadTicket: String?,
    ): String {
        require(path.startsWith('/') && !path.startsWith("//") && '\\' !in path && path.none { it.code < 32 }) {
            "Upload path must be an API-relative path"
        }
        require(active == null) { "This uploader already has an active request" }
        return suspendCancellableCoroutine { continuation ->
            val xhr = XMLHttpRequest()
            active = xhr
            fun cleanup() {
                if (active === xhr) active = null
                xhr.onload = null
                xhr.onerror = null
                xhr.onabort = null
                xhr.ontimeout = null
                xhr.upload.onprogress = null
            }
            fun fail(failure: Throwable) {
                cleanup()
                if (continuation.isActive) continuation.resumeWithException(failure)
            }
            try {
                xhr.open("PUT", "${ApiConfig.baseUrl}$path", true)
                xhr.setRequestHeader("Accept", "application/json")
                xhr.setRequestHeader("Content-Type", contentType)
                if (uploadTicket == null) {
                    Session.token?.takeIf(String::isNotBlank)?.let { xhr.setRequestHeader("Authorization", "Bearer $it") }
                } else {
                    xhr.setRequestHeader("Lingvo-Upload-Ticket", uploadTicket)
                }
                ifMatch?.let { xhr.setRequestHeader("If-Match", it) }
                ifNoneMatch?.let { xhr.setRequestHeader("If-None-Match", it) }
                xhr.upload.onprogress = { event ->
                    if (event.lengthComputable) onProgress(event.loaded.toLong(), event.total.toLong())
                }
                xhr.onload = {
                    val status = xhr.status.toInt()
                    val text = xhr.responseText
                    if (status in 200..299) {
                        cleanup()
                        if (continuation.isActive) continuation.resume(text)
                    } else {
                        val error = runCatching { API_JSON.decodeFromString<ApiErrorBody>(text).error }.getOrNull()
                        fail(ApiException(status, error?.code ?: "http_$status", error?.message ?: genericFailure(status),
                            error?.details ?: JsonObject(emptyMap())))
                    }
                }
                xhr.onerror = { fail(ApiException(0, "network_error", "Couldn't reach the Lingvo server.")) }
                xhr.ontimeout = { fail(ApiException(0, "request_timeout", "Couldn't reach the Lingvo server.")) }
                xhr.onabort = { fail(CancellationException("Upload cancelled")) }
                continuation.invokeOnCancellation { cleanup(); xhr.abort() }
                xhr.send(body)
            } catch (failure: Throwable) {
                fail(failure)
            }
        }
    }
}
